import React, { useState, useEffect } from 'react';

import { Button } from 'app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { Pencil, Trash, Plus, Search, Folder } from 'lucide-react';
import { Input } from 'app/components/ui/input';
import { Badge } from 'app/components/ui/badge';
import { useProductsSlice } from '../../slice';
import { DialogUpdateProductCategories } from '../Edit/edit-dialog';
import { AlertProductCategoriesDelete } from '../Delete/delete-confirmation';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useDebounce from 'utils/hooks/debounce-hook';
import DialogAddCategory from '../Add/categories-dialog';
import { mediaBaseUrl } from 'utils/api/endpoints';

export function DataTable() {
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openAdd, setOpenAdd] = useState(false);

  const { useLazyGetProductsCategoriesQuery } = useProductsSlice();

  const [getCategories, { data: listData, isLoading: listLoading }] =
    useLazyGetProductsCategoriesQuery();

  const categoriesPagination = {
    totalPages: listData?.response?.totalPages,
    pageSize: listData?.response?.itemsPerPage,
  };

  const categories = listData?.response?.data || [];

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'category_id',
      header: 'ID',
      cell: ({ row }) => (
        <div className="font-mono text-xs text-gray-500 dark:text-gray-400 text-center">
          #{row.getValue('category_id')}
        </div>
      ),
    },
    {
      accessorKey: 'category_name',
      header: 'Category Name',
      cell: ({ row }) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <Folder className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {row.getValue('category_name')}
            </span>
            {row.original.category_path && (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {row.original.category_path}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'depth',
      header: 'Level',
      cell: ({ row }) => (
        <div className="text-center">
          <Badge variant="secondary" className="text-xs">
            Level {row.getValue('depth')}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: 'image',
      header: 'Image',
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          {row.getValue('image') ? (
            <div className="relative">
              <img
                src={mediaBaseUrl + '/products/' + row.getValue('image')}
                alt="Category"
                className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-lg transition-colors duration-200"></div>
            </div>
          ) : (
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-600">
              <Folder className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedRow(row.original);
              setOpenUpdate(true);
            }}
            className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700"
            title="Edit Category"
          >
            <Pencil className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedRow(row.original);
              setOpenDelete(true);
            }}
            className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
            title="Delete Category"
          >
            <Trash className="w-4 h-4 text-red-600 dark:text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: categories,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: categoriesPagination?.totalPages,
    rowCount: categoriesPagination?.pageSize,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  // Load categories data
  useEffect(() => {
    getCategories({
      page: pagination.pageIndex + 1,
      search: searchDebounce,
      includeSubcategories: true,
    });
  }, [pagination, searchDebounce]);

  return (
    <div className="p-6">
      {/* Search and Controls Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => setOpenAdd(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all duration-200 h-10 px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow
                key={headerGroup.id}
                className="border-gray-200 dark:border-gray-700"
              >
                {headerGroup.headers.map((header, index) => (
                  <TableHead
                    key={header.id}
                    className={`text-gray-700 dark:text-gray-300 font-semibold ${index === 0
                        ? 'text-center w-20'
                        : index === 1
                          ? 'text-left w-auto'
                          : index === 2
                            ? 'text-center w-32'
                            : 'text-center w-32'
                      }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors duration-200"
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={`py-4 ${index === 0
                          ? 'text-center'
                          : index === 1
                            ? 'text-left'
                            : index === 2
                              ? 'text-center'
                              : index === 3
                                ? 'text-center'
                                : 'text-center'
                        }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-32 text-center text-gray-500 dark:text-gray-400"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Folder className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                    <span>No categories found</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing page {pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-8 w-8 p-0"
          >
            {'<<'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-8 px-3"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-8 px-3"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
            className="h-8 w-8 p-0"
          >
            {'>>'}
          </Button>
        </div>
      </div>

      <DialogAddCategory isOpen={openAdd} onClose={setOpenAdd} />

      <DialogUpdateProductCategories
        isOpen={openUpdate}
        onOpenChange={setOpenUpdate}
        Categoriesdata={selectedRow}
      />
      <AlertProductCategoriesDelete
        isOpen={openDelete}
        onOpenChange={setOpenDelete}
        data={selectedRow}
      />
    </div>
  );
}
